import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Subject } from 'rxjs';

import { User } from '../models/user';
import { AuthenticationService } from '../services/security/authentication.service';
import { localStorageAccessConstant } from '../constants/LocalStorageAccessConstant';
import { UserRole } from '../enums/user-role';
import { Router } from '@angular/router';
import { accountTypeChangedEvent, fboChangedEvent } from '../constants/sharedEvents';
import { StorageService } from '../services/security/storage.service';

export interface ActiveUser {
    fboId: number;
    groupId: number;
    icao: string;
}

export class DashboardSettings {
    filterStartDate: Date;
    filterEndDate: Date;
    widgetOrder: string[];
}

export class AnalyticsSettings {
    widgetOrder: string[];
}
@Injectable({
    providedIn: 'root',
})
export class SharedService {
    dashboardSettings: DashboardSettings = new DashboardSettings();
    analyticsSettings: AnalyticsSettings = new AnalyticsSettings();

    priceTemplateMessageSource = new BehaviorSubject('Update Pricing Template');
    currentMessage = this.priceTemplateMessageSource.asObservable();

    priceUpdateMessage = new BehaviorSubject('Enable button');
    priceMessage = this.priceUpdateMessage.asObservable();

    //for Log History
    updatedHistory = new BehaviorSubject<boolean>(false);

    // Observable string sources
    emitChangeSource = new Subject();
    valueChangeSource = new Subject();

    // Observable string streams
    changeEmitted$ = this.emitChangeSource.asObservable();
    valueChanged$ = this.valueChangeSource.asObservable();

    constructor(private authenticationService: AuthenticationService, 
        private router: Router,
        private ls: StorageService) {
        this.authenticationService.currentUser.subscribe(
            (x) => (this.currentUser = x)
        );
        const storedDashboardSettings =
            this.ls.get(localStorageAccessConstant.dashboardSettings);
        if (!storedDashboardSettings) {
            this.dashboardSettings.filterStartDate = new Date(
                moment().add(-6, 'M').format('MM/DD/YYYY')
            );
            this.dashboardSettings.filterEndDate = new Date(
                moment().format('MM/DD/YYYY')
            );
        } else {
            this.dashboardSettings = JSON.parse(storedDashboardSettings);
        }

        const storedAnalyticsSettings =
            this.ls.get(localStorageAccessConstant.analyticsSettings);
        if (storedAnalyticsSettings) {
            this.analyticsSettings = JSON.parse(storedAnalyticsSettings);
        }
    }

    // Private members
    private _currentUser: User;
    private _title: string;
    private _supportUrl: string;
    private _memberUrl: string;
    get title(): string {
        return this._title;
    }
    set title(title: string) {
        this._title = title;
    }
    // Public Members
    get currentUser(): User {
        // const error = new Error();
        // const stackTrace = error.stack;
        // if(stackTrace.includes('currentUser')) return;


        // console.log("🚀 ~ file: shared-service.ts:72 ~ SharedService ~ getcurrentUser ~ this._currentUser == null:", this._currentUser)

        // for (const key in localStorageAccessConstant) {
        //     if (!this._currentUser[key] && this.ls.get(key)) {
        //         this._currentUser[key] = (this._currentUser[key] instanceof Number) ? Number(this.ls.get(localStorageAccessConstant.fboId)) : this.ls.get(localStorageAccessConstant.fboId);
        //     }
        // }

        if (this._currentUser == null) return;

        if (!this._currentUser.accountType && this.ls.get(localStorageAccessConstant.accountType)) {
            this._currentUser.accountType = Number(this.ls.get(localStorageAccessConstant.accountType));
        }
        // users/current returns the signed-in user's home location; an impersonation tab or an airport
        // switch stores this session's own, which must survive a reload.
        for (const key of [localStorageAccessConstant.icao, localStorageAccessConstant.isSingleSourceFbo, localStorageAccessConstant.isNetworkFbo]) {
            const sessionValue = this.ls.get(key);
            if (sessionValue !== null && sessionValue !== undefined && sessionValue !== '') {
                (this._currentUser as any)[key] = sessionValue;
            }
        }
        if (!this._currentUser.fboId && this.ls.get(localStorageAccessConstant.fboId)) {
            this._currentUser.fboId = Number(this.ls.get(localStorageAccessConstant.fboId));
        }
        if (
            !this._currentUser.managerGroupId &&
            this.ls.get(localStorageAccessConstant.managerGroupId)
        ) {
            this._currentUser.managerGroupId = Number(
                this.ls.get(localStorageAccessConstant.managerGroupId)
            );
        }
        if (
            !this._currentUser.impersonatedRole &&
            this.ls.get(localStorageAccessConstant.impersonatedrole)
        ) {
            this._currentUser.impersonatedRole = Number(
                this.ls.get(localStorageAccessConstant.impersonatedrole)
            );
        }
        const sessionGroupId = this.ls.get(localStorageAccessConstant.groupId);
        if (
            sessionGroupId &&
            (!this._currentUser.groupId ||
                this._currentUser.groupId !== Number(sessionGroupId))
        ) {
            this._currentUser.groupId = Number(sessionGroupId);
        }
        if (!this._currentUser.groupId && this.ls.get(localStorageAccessConstant.groupId)) {
            this._currentUser.groupId = Number(this.ls.get(localStorageAccessConstant.groupId));
        }
        if (
            !this._currentUser.conductorFbo &&
            this.ls.get(localStorageAccessConstant.conductorFbo)
        ) {
            //this._currentUser.conductorFbo  = this.ls.get<boolean>(localStorageAccessConstant.conductorFbo);
            this._currentUser.conductorFbo  = Boolean(this.ls.get(localStorageAccessConstant.conductorFbo));
        }
        return this._currentUser;
    }
    get isCsr() {
        return this.currentUser.role === UserRole.CSR;
    }

    get isFboLogin() {
        return this.currentUser.fboId > 0 ? true : false;
    }
    set currentUser(user: User) {
        this._currentUser = user;
    }

    NotifyPricingTemplateComponent(message: string) {
        this.priceTemplateMessageSource.next(message);
    }

    get isConductorUser(): boolean {
        return this.currentUser.role == UserRole.Conductor ? true : false;
    }

    set supportUrl(url: string) {
        this._supportUrl = url;
    }

    get supportUrl(): string {
        return this._supportUrl;
    }

    set memberUrl(url: string) {
        this._memberUrl = url;
    }

    get memberUrl(): string {
        return this._memberUrl;
    }

    // Service message commands
    emitChange(change: string) {
        this.emitChangeSource.next(change);
    }

    valueChange(change: any) {
        this.valueChangeSource.next(change);
    }

    isManagingGroup(): boolean {
        return (this.currentUser.groupId > 0 &&
            this.currentUser.managerGroupId > 0 &&
            this.currentUser.groupId != this.currentUser.managerGroupId);
    }
    setCurrentUserPropertyValue<T>(property: string, value: T): void{
        this.currentUser[property] = value;
        this.ls.set(localStorageAccessConstant[property],value?.toString());
    }
    resetCurrentUserPropertyValue(property: string, resetvalue: any =  null): void{
        this.currentUser[property] = resetvalue;
        this.ls.remove(property);
    }
    
    getCurrentUserPropertyValue(property: string): string | null;
    getCurrentUserPropertyValue<T>(property: string): T  | null;
    getCurrentUserPropertyValue<T>(property: string): T  | null {
        const inMemoryValue = (this.currentUser as any)?.[property];
        if (inMemoryValue !== undefined && inMemoryValue !== null) {
            return inMemoryValue as T;
        }
        const stored = this.ls.get(property);
        return stored as T;
    }
    

    setLocalStorageForImpersonation(groupId, fboId, accountType, icao, isSingleSourceFbo, isNetworkFbo) {

        this.ls.remove(localStorageAccessConstant.managerGroupId);
        this.ls.remove(localStorageAccessConstant.groupId);
        this.ls.remove(localStorageAccessConstant.impersonatedrole);
        this.ls.remove(localStorageAccessConstant.conductorFbo);
        this.ls.remove(localStorageAccessConstant.fboId);
        this.ls.remove(localStorageAccessConstant.icao);
        this.ls.remove(localStorageAccessConstant.accountType);
        this.ls.remove(localStorageAccessConstant.isNetworkFbo);
        this.ls.remove(localStorageAccessConstant.isSingleSourceFbo);

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.managerGroupId,
            this.currentUser.groupId
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.groupId,
            groupId
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.impersonatedrole,
            UserRole.Primary
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.conductorFbo,
            true
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.fboId,
            fboId
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.icao,
            icao
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.accountType,
            accountType
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.isNetworkFbo,
            isNetworkFbo
        );

        this.setCurrentUserPropertyValue(
            localStorageAccessConstant.isSingleSourceFbo,
            isSingleSourceFbo
        );

        this.emitChange(fboChangedEvent);
        this.emitChange(accountTypeChangedEvent);

        this.router.navigate([
            '/default-layout/dashboard-fbo-updated/',
        ]);
    }
}
